// @flow
import React, { useEffect, useState } from 'react';
import Avatar from '@atlaskit/avatar';
import Tooltip from '@atlaskit/tooltip';
import styled from 'styled-components';
import LinkFilledIcon from '@atlaskit/icon/glyph/link-filled';
import { CommentAuthor, CommentTime, CommentEdited } from '@atlaskit/comment';
import { createLocalizationProvider, LocalizationProvider } from '@atlaskit/locale';
import { formatDistanceToNow } from 'date-fns';
import { ASSIGNEE_TYPES_OBJECT } from '../assets/assigneeTypes.assets';
import { getJiraProjects } from '../../services/jira-services';

const HistoryLogsTabWrapper = styled.div`
    padding-top: 10px;
`;

const CommentWrapper = styled.div`
    padding-top: 20px;
    display: flex;
`;

const ChangeContent = styled.div`
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  margin-top: 8px;
  color: var(--ds-text-subtle, #42526E);
}
`;

const Edited = styled.div`
  margin-top: 8px;
}
`;

const ChangeContentCol = styled.div`
    max-width: 50%;
`;

const ChangeContentColArrow = styled.div`
    margin: 0px 8px;
    font-size: 20px;
    margin-top: -8px;
`;

const AvatarWrapper = styled.div`
    padding-right: 10px;
`;

const AuthorTimeWrapper = styled.div`
    display: flex;
`;

const CommentTimeWrapper = styled.div`
    padding-left: 10px;
`;

const localizationProvider: LocalizationProvider = createLocalizationProvider(window.navigator.language);

const constructEvent = (action, component, jiraProjects) => {
    const { changeType, changeValue, oldValue } = action;
    let edited;
    let content = (
        <ChangeContent>
            <ChangeContentCol>{oldValue || 'None'}</ChangeContentCol>
            <ChangeContentColArrow> → </ChangeContentColArrow>
            <ChangeContentCol>{changeValue || 'None'}</ChangeContentCol>
        </ChangeContent>
    );
    switch (changeType) {
        case 'create':
            edited = (
                <Edited>
                    created the <b>Component</b>
                </Edited>
            );
            content = (
                <ChangeContent>
                    <span>
                        Created <b>{changeValue}</b> component
                    </span>
                </ChangeContent>
            );
            break;
        case 'update_title':
            edited = (
                <Edited>
                    updated the <b>Title</b>
                </Edited>
            );
            break;
        case 'update_description':
            edited = (
                <Edited>
                    updated the <b>Description</b>
                </Edited>
            );
            break;
        case 'update_projects_remove':
            edited = (
                <Edited>
                    removed a <b>Project</b>
                </Edited>
            );
            content = (
                <ChangeContent>
                    <span>
                        removed <Project ProjectId={changeValue} jiraProjects={jiraProjects} /> project
                    </span>
                </ChangeContent>
            );
            break;
        case 'update_projects_add':
            edited = (
                <Edited>
                    added a <b>Project</b>
                </Edited>
            );
            content = (
                <ChangeContent>
                    <span>
                        added <Project ProjectId={changeValue} jiraProjects={jiraProjects} /> project
                    </span>
                </ChangeContent>
            );
            break;
        case 'update_component_lead':
            edited = (
                <Edited>
                    updated a <b>Component lead</b>
                </Edited>
            );
            break;
        case 'update_assignee':
            edited = (
                <Edited>
                    updated the <b>Assignee</b>
                </Edited>
            );
            content = (
                <ChangeContent>
                    <ChangeContentCol>{ASSIGNEE_TYPES_OBJECT[oldValue]}</ChangeContentCol>
                    <ChangeContentColArrow> → </ChangeContentColArrow>
                    <ChangeContentCol>{ASSIGNEE_TYPES_OBJECT[changeValue]}</ChangeContentCol>
                </ChangeContent>
            );
            break;
        default:
            break;
    }
    return {
        edited,
        content,
    };
};

let Project = ({ ProjectId, jiraProjects }) => {
    const projectFound = jiraProjects.find((project) => ProjectId === project.id);
    const projectName = projectFound ? `${projectFound.key} - ${projectFound.name}` : `${ProjectId}`;
    return <b>{projectName}</b>;
};
Project = React.memo(Project);

const TimeAgo = ({ date }) => {
    const getParsedDate = (dateToParse) => {
        const options = {
            addSuffix: true,
            includeSeconds: false,
        };
        return formatDistanceToNow(dateToParse, options);
    };

    return <p>{getParsedDate(date)}</p>;
};

let ChangeLog = ({ action, component, jiraProjects }) => {
    const user = {
        name: 'Someone',
        avatar: null,
    };
    if (action.user && action.user.displayName) {
        user.name = action.user.displayName;
    }
    if (action.user && action.user.avatar) {
        user.avatar = action.user.avatar;
    }
    const createdAtTooltip = `${localizationProvider.formatDate(
        new Date(action.createdAt),
    )} ${localizationProvider.formatTime(new Date(action.createdAt))}`;
    const eventLog = constructEvent(action, component, jiraProjects);
    return (
        <CommentWrapper>
            <AvatarWrapper>
                <Avatar src={user.avatar} label={user.name} size="medium" />
            </AvatarWrapper>
            <div>
                <AuthorTimeWrapper>
                    <CommentAuthor>{user.name}</CommentAuthor>
                    <CommentTimeWrapper>
                        <CommentTime>
                            <Tooltip content={createdAtTooltip} position="top">
                                <TimeAgo date={new Date(action.createdAt)} />
                            </Tooltip>
                        </CommentTime>
                    </CommentTimeWrapper>
                </AuthorTimeWrapper>
                <div>
                    <CommentEdited>{eventLog.edited}</CommentEdited>
                </div>
                <div>{eventLog.content}</div>
            </div>
        </CommentWrapper>
    );
};
ChangeLog = React.memo(ChangeLog);

function HistoryLogsTab({ historyLogs, component }) {
    const [jiraProjects, setJiraProjects] = useState([]);

    useEffect(() => {
        const projectIds = (historyLogs ?? []).reduce((acc, curr) => {
            if (
                ['update_projects_add', 'update_projects_remove'].includes(curr.changeType) &&
                !acc.includes(curr.changeValue)
            ) {
                acc.push(curr.changeValue);
            }
            return acc;
        }, []);

        (async () => {
            const projects = await getJiraProjects({
                jiraProjectIds: projectIds,
                maxResultsPerPage: 50,
                maxPages: 1,
            });
            setJiraProjects(projects);
        })();
    }, [historyLogs]);

    return (
        <HistoryLogsTabWrapper>
            {historyLogs.length === 0 && (
                <div>
                    <LinkFilledIcon /> <span style={{ verticalAlign: 5 }}>No history found</span>
                </div>
            )}
            {historyLogs
                .sort((prev, next) => (prev.createdAt > next.createdAt ? -1 : 1))
                .map((historyLog) => {
                    return (
                        <ChangeLog
                            key={historyLog.id}
                            action={historyLog}
                            component={component}
                            jiraProjects={jiraProjects}
                        />
                    );
                })}
        </HistoryLogsTabWrapper>
    );
}

export default HistoryLogsTab;
